import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import { isValidHttpUrl } from '../../lib/helpers';
import useCampusCountryCurrency from '../../lib/hooks/useCampusCountryCurrency';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodSelector, chosenCoursePeriodsSelector } from '../../reducers/selectors';

import './index.scss';

const ProgrammeCostsPage = ({
  programmeCostsSupportingUrls,
  dispatchAddPageData,
  dispatchWizardNav,
  tuitionCost,
  deposit,
  livingExpenses,
  currentPeriod,
  periods,
}) => {
  const [urlInputs, setUrlInputs] = useState({
    programmeCostsSupportingUrlFirst: programmeCostsSupportingUrls[0] || '',
    programmeCostsSupportingUrlSecond: programmeCostsSupportingUrls[1] || '',
    programmeCostsSupportingUrlThird: programmeCostsSupportingUrls[2] || '',
  });

  const [urlErrors, setUrlErrors] = useState({
    programmeCostsSupportingUrlFirst: '',
    programmeCostsSupportingUrlSecond: '',
    programmeCostsSupportingUrlThird: '',
  });

  const [costsInputs, setCostsInputs] = useState({
    tuitionCostAmount: tuitionCost.amount,
    depositAmount: deposit.amount,
    livingExpensesAmount: livingExpenses.amount,
  });

  const [costsErrors, setCostsErrors] = useState({
    tuitionCostAmount: '',
    depositAmount: '',
    livingExpensesAmount: '',
  });

  const [initalCurrency, loanCurrencies] = useCampusCountryCurrency();
  const [currency, setCurrency] = useState(initalCurrency);

  const isTuitionCostFilledIn = costsInputs.tuitionCostAmount !== '';
  const isDepositFilledIn = costsInputs.depositAmount !== '';
  const isLivingExpensesFilledIn = costsInputs.livingExpensesAmount !== '';
  const isFirstUrlFilledIn = urlInputs.programmeCostsSupportingUrlFirst !== '';
  const isSecondUrlFilledIn = urlInputs.programmeCostsSupportingUrlSecond !== '';
  const isThirdUrlFilledIn = urlInputs.programmeCostsSupportingUrlThird !== '';

  const handleSubmit = event => {
    event.preventDefault();

    if (!allFieldsValid) return;

    analytics.sendEvent('Click - Next', 'programme-costs');

    const tuitionCostPayload = payloadFactory.createAddPageDataPayload('tuitionCost', {
      currency: currency,
      amount: costsInputs.tuitionCostAmount,
    });
    const depositPayload = payloadFactory.createAddPageDataPayload('deposit', {
      currency: currency,
      amount: costsInputs.depositAmount,
    });
    const livingExpensesPayload = payloadFactory.createAddPageDataPayload('livingExpenses', {
      currency: currency,
      amount: costsInputs.livingExpensesAmount,
    });
    const programmeCostsSupportingUrlsPayload = payloadFactory.createAddPageDataPayload(
      'programmeCostsSupportingUrls',
      Object.values(urlInputs).filter(url => url),
    );

    dispatchAddPageData(tuitionCostPayload);
    dispatchAddPageData(depositPayload);
    dispatchAddPageData(livingExpensesPayload);
    dispatchAddPageData(programmeCostsSupportingUrlsPayload);

    if (currentPeriod !== null || periods.length !== 0) {
      const resetPeriodTimeStampPayload = payloadFactory.createAddPageDataPayload(
        'periodsTimeStamp',
        null,
      );
      dispatchAddPageData(resetPeriodTimeStampPayload);
    }

    track('Submit - Cost Info', {
      tuition_cost: costsInputs.tuitionCostAmount,
      deposit: costsInputs.depositAmount,
      living_expenses: costsInputs.livingExpensesAmount,
      url_1: urlInputs.programmeCostsSupportingUrlFirst,
      url_2: urlInputs.programmeCostsSupportingUrlSecond,
      url_3: urlInputs.programmeCostsSupportingUrlThird,
    });

    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleCostInputChange = e => {
    const { name, value } = e.target;

    setCostsInputs({
      ...costsInputs,
      [name]: value,
    });

    validateCostField(name, value);
  };

  let costErrorTimeout;
  const validateCostField = (fieldName, value) => {
    let errorMessage = '';

    switch (fieldName) {
      case 'tuitionCostAmount':
        errorMessage = value < 0 ? 'The tuition cost amount cannot be negative.' : '';

        break;

      case 'depositAmount':
        if (value < 0) {
          errorMessage = 'The deposit cannot be negative.';
        } else if (isTuitionCostFilledIn && Number(value) > Number(costsInputs.tuitionCostAmount)) {
          errorMessage = 'The deposit cannot exceed the tuition cost.';
        } else {
          errorMessage = '';
        }

        break;

      case 'livingExpensesAmount':
        errorMessage = value < 0 ? 'The living expenses cannot be negative.' : '';

        break;

      default:
        break;
    }

    if (costErrorTimeout) clearTimeout(costErrorTimeout);
    costErrorTimeout = setTimeout(() => {
      setCostsErrors({
        ...costsErrors,
        [fieldName]: errorMessage,
      });
    }, 500);
  };

  let urlErrorTimeout;
  const handleUrlInputChange = e => {
    const { name, value } = e.target;

    setUrlInputs({
      ...urlInputs,
      [name]: value,
    });

    if (urlErrorTimeout) clearTimeout(urlErrorTimeout);
    urlErrorTimeout = setTimeout(() => validateUrlField(name, value), 500);
  };

  const validateUrlField = (fieldName, value) => {
    let errorMessage = '';

    switch (fieldName) {
      case 'programmeCostsSupportingUrlFirst':
      case 'programmeCostsSupportingUrlSecond':
      case 'programmeCostsSupportingUrlThird':
        errorMessage = !isValidHttpUrl(value)
          ? 'Please enter a valid URL starting with http:// or https://'
          : '';

        break;

      default:
        break;
    }

    setUrlErrors({
      ...urlErrors,
      [fieldName]: errorMessage,
    });
  };

  const allFieldsValid =
    isTuitionCostFilledIn &&
    !costsErrors.tuitionCostAmount &&
    isLivingExpensesFilledIn &&
    !costsErrors.livingExpensesAmount &&
    (isFirstUrlFilledIn || isSecondUrlFilledIn || isThirdUrlFilledIn) &&
    (isFirstUrlFilledIn ? !urlErrors.programmeCostsSupportingUrlFirst : true) &&
    (isSecondUrlFilledIn ? !urlErrors.programmeCostsSupportingUrlSecond : true) &&
    (isThirdUrlFilledIn ? !urlErrors.programmeCostsSupportingUrlThird : true) &&
    (isDepositFilledIn ? !costsErrors.depositAmount : true);

  const renderError = (isFieldFilledIn, errorMessage) => {
    if (isFieldFilledIn && errorMessage) {
      return <div className="text-error desktop-d6">{errorMessage}</div>;
    }

    return null;
  };

  const helpText =
    'If the tuition cost is in a currency that’s not listed, please convert it to USD';

  return (
    <PageLayout
      title="What’s the tuition cost and living expenses for the entire programme?"
      helpText={helpText}
    >
      <div className="programme-costs">
        <form>
          <div className="row">
            <div className="px-2 my-4 w-full">
              <div>
                <p className="text-field__input-header form-label mb-0">
                  Total cost of tuition (including deposit if applicable)
                </p>
                <CombinedTextField>
                  <Select
                    id="tuition-cost-currency"
                    data-testid="tuition-cost-currency"
                    onChange={e => {
                      setCurrency(e.currentTarget.value);
                    }}
                    value={currency}
                    options={loanCurrencies}
                  />

                  <InputField
                    type="number"
                    id="tuitionCostAmount"
                    data-testid="tuition-cost-amount"
                    placeholder="Enter tuition cost amount"
                    value={costsInputs.tuitionCostAmount}
                    onChange={handleCostInputChange}
                    error={isTuitionCostFilledIn && costsErrors.tuitionCostAmount.length > 0}
                  />
                </CombinedTextField>
              </div>
              {renderError(isTuitionCostFilledIn, costsErrors.tuitionCostAmount)}

              <div className="mt-4">
                <p className="text-field__input-header form-label mb-0">
                  Deposit required (if applicable)
                </p>
                <CombinedTextField>
                  <Select
                    id="deposit-currency"
                    data-testid="deposit-currency"
                    onChange={e => {
                      setCurrency(e.currentTarget.value);
                    }}
                    value={currency}
                    options={loanCurrencies}
                  />

                  <InputField
                    type="number"
                    id="depositAmount"
                    data-testid="deposit-amount"
                    placeholder="Enter deposit amount"
                    value={costsInputs.depositAmount}
                    onChange={handleCostInputChange}
                    error={isDepositFilledIn && costsErrors.depositAmount.length > 0}
                  />
                </CombinedTextField>
              </div>
              {renderError(isDepositFilledIn, costsErrors.depositAmount)}

              <div className="mt-4">
                <p className="text-field__input-header form-label mb-0">Expected living expenses</p>
                <CombinedTextField>
                  <Select
                    id="living-expenses-currency"
                    data-testid="living-expenses-currency"
                    onChange={e => {
                      setCurrency(e.currentTarget.value);
                    }}
                    value={currency}
                    options={loanCurrencies}
                  />

                  <InputField
                    type="number"
                    id="livingExpensesAmount"
                    data-testid="living-expenses-amount"
                    placeholder="Enter living expenses amount"
                    value={costsInputs.livingExpensesAmount}
                    onChange={handleCostInputChange}
                    error={isLivingExpensesFilledIn && costsErrors.livingExpensesAmount.length > 0}
                  />
                </CombinedTextField>
              </div>
              {renderError(isLivingExpensesFilledIn, costsErrors.livingExpensesAmount)}
            </div>
          </div>

          <div className="mt-4">
            <p>
              We need to validate the above information so please tell us the URL where you found
              them.
            </p>
            <div className="row">
              <div className="px-2 my-4 w-full">
                <div className="mt-2">
                  <InputField
                    type="string"
                    id="programmeCostsSupportingUrlFirst"
                    data-testid="supporting-url-1"
                    placeholder=""
                    label="Page URL"
                    value={urlInputs.programmeCostsSupportingUrlFirst}
                    onChange={handleUrlInputChange}
                    error={
                      isFirstUrlFilledIn && urlErrors.programmeCostsSupportingUrlFirst.length > 0
                    }
                  />
                </div>
                {renderError(isFirstUrlFilledIn, urlErrors.programmeCostsSupportingUrlFirst)}

                <div className="mt-2">
                  <InputField
                    type="string"
                    id="programmeCostsSupportingUrlSecond"
                    placeholder=""
                    label="Page URL"
                    value={urlInputs.programmeCostsSupportingUrlSecond}
                    onChange={handleUrlInputChange}
                    error={
                      isSecondUrlFilledIn && urlErrors.programmeCostsSupportingUrlSecond.length > 0
                    }
                  />
                </div>
                {renderError(isSecondUrlFilledIn, urlErrors.programmeCostsSupportingUrlSecond)}

                <div className="mt-2">
                  <InputField
                    type="string"
                    id="programmeCostsSupportingUrlThird"
                    placeholder=""
                    label="Page URL"
                    value={urlInputs.programmeCostsSupportingUrlThird}
                    onChange={handleUrlInputChange}
                    error={
                      isThirdUrlFilledIn && urlErrors.programmeCostsSupportingUrlThird.length > 0
                    }
                  />
                </div>
                {renderError(isThirdUrlFilledIn, urlErrors.programmeCostsSupportingUrlThird)}
              </div>
            </div>
          </div>

          <div className="mt-12 mb-12">
            <Button
              type="submit"
              className="niso-button"
              onClick={e => handleSubmit(e)}
              isDisabled={!allFieldsValid}
            >
              Next
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

ProgrammeCostsPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  tuitionCost: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  deposit: PropTypes.shape({
    amount: PropTypes.string,
    currency: PropTypes.string,
  }),
  livingExpenses: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  programmeCostsSupportingUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentPeriod: PropTypes.shape({
    periodName: PropTypes.string,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  periods: PropTypes.array,
};

ProgrammeCostsPage.defaultProps = {
  tuitionCost: { currency: '', amount: '' },
  deposit: { currency: '', amount: '' },
  livingExpenses: { currency: '', amount: '' },
  programmeCostsSupportingUrls: [],
};

export const mapStateToProps = state => ({
  programmeCostsSupportingUrls: state.builder.ui.programmeCostsSupportingUrls,
  tuitionCost: state.builder.ui.tuitionCost,
  deposit: state.builder.ui.deposit,
  livingExpenses: state.builder.ui.livingExpenses,
  currentPeriod: periodSelector(state),
  periods: chosenCoursePeriodsSelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { ProgrammeCostsPage };

export default connect(mapStateToProps, mapDispatchToProps)(ProgrammeCostsPage);
